import { motion } from "framer-motion";
import { ArrowRight, MessageCircleHeart } from "lucide-react";
import { useLang } from "@/lib/i18n";

const HERO_IMG =
  "https://images.pexels.com/photos/8535230/pexels-photo-8535230.jpeg?auto=compress&cs=tinysrgb&w=1600";

export default function Hero({ onFindActivity, onTalk }) {
  const { t } = useLang();
  return (
    <section id="top" className="relative pt-28 md:pt-32 pb-16 md:pb-24 overflow-hidden" data-testid="hero-section">
      <ConfettiBg />
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative">
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-center">
          <div className="col-span-12 lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="chip"
              data-testid="hero-chip"
            >
              <span className="w-2 h-2 rounded-full bg-primary" />
              {t("hero.chip")}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.08 }}
              className="mt-6 text-sm md:text-base font-bold uppercase tracking-[0.14em] text-primary"
            >
              {t("hero.surtitre")}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-4 font-heading text-4xl sm:text-5xl md:text-6xl lg:text-[68px] leading-[1.02] text-foreground"
            >
              {t("hero.title_1")}{" "}
              <span className="relative inline-block">
                <span className="relative z-10">{t("hero.title_avance")}</span>
                <span
                  aria-hidden="true"
                  className="absolute left-0 right-0 bottom-1 md:bottom-2 h-3 md:h-4 rounded-full -z-0"
                  style={{ backgroundColor: "hsl(var(--accent))", opacity: 0.6 }}
                />
              </span>
              {t("hero.title_2")}{" "}
              <span className="text-primary">{t("hero.title_comprise")}</span>.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.28 }}
              className="mt-6 text-lg md:text-xl leading-relaxed text-foreground/85 max-w-2xl"
            >
              {t("hero.desc")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <button
                onClick={onFindActivity}
                data-testid="hero-find-activity-btn"
                className="pill-btn bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-[0_10px_30px_-8px_rgba(15,92,94,0.5)]"
              >
                {t("hero.cta.find")}
                <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </button>
              <button
                onClick={onTalk}
                data-testid="hero-talk-btn"
                className="pill-btn bg-white border-2 border-foreground text-foreground hover:bg-foreground hover:text-background"
              >
                <MessageCircleHeart className="w-4 h-4" strokeWidth={2.5} />
                {t("hero.cta.talk")}
              </button>
              <a
                href="#a-propos"
                className="text-sm font-semibold text-foreground/75 hover:text-primary underline underline-offset-4 ml-2"
                data-testid="hero-discover-link"
              >
                {t("hero.cta.discover")}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-foreground/70"
              data-testid="hero-trust"
            >
              <Trust dot="dot-teal" text={t("hero.trust.since")} />
              <Trust dot="dot-coral" text={t("hero.trust.jeunesse")} />
              <Trust dot="dot-yellow" text={t("hero.trust.open")} />
            </motion.div>
          </div>

          <div className="col-span-12 lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] shadow-[0_30px_80px_-30px_rgba(15,92,94,0.45)]">
                <img
                  src={HERO_IMG}
                  alt={t("common.demo_visual_alt")}
                  className="w-full h-full object-cover"
                />
              </div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="absolute -left-4 md:-left-8 top-8 bg-white rounded-2xl shadow-lg px-4 py-3 border border-border max-w-[210px]"
              >
                <div className="text-xs uppercase tracking-widest text-primary font-bold">
                  {t("hero.promise")}
                </div>
                <div className="mt-1 font-heading text-base leading-tight">
                  {t("hero.promise_text")}
                </div>
              </motion.div>

              <div
                aria-hidden="true"
                className="absolute -right-2 md:-right-5 -bottom-5 w-24 h-24 rounded-full flex items-center justify-center font-heading text-white text-sm text-center leading-tight px-2"
                style={{ backgroundColor: "hsl(var(--coral))" }}
              >
                {t("hero.since")}
              </div>
              <div
                aria-hidden="true"
                className="absolute -left-6 bottom-16 w-16 h-16 rounded-full"
                style={{ backgroundColor: "hsl(var(--accent))" }}
              />
            </motion.div>

            <p className="mt-4 text-xs text-foreground/50 text-right">{t("hero.visual_note")}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Trust({ dot, text }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />
      <span className="font-semibold">{text}</span>
    </span>
  );
}

function ConfettiBg() {
  return (
    <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
      <div className="absolute top-16 left-[8%] w-3 h-3 rounded-full dot-yellow" />
      <div className="absolute top-40 left-[22%] w-2 h-2 rounded-full dot-coral" />
      <div className="absolute top-24 right-[35%] w-2.5 h-2.5 rounded-full dot-sage" />
      <div className="absolute top-52 right-[28%] w-2 h-2 rounded-full dot-plum" />
      <div className="absolute bottom-24 left-[14%] w-2 h-2 rounded-full dot-lime" />
      <div className="absolute bottom-40 left-[36%] w-3 h-3 rounded-full dot-sky" />
      <div className="absolute bottom-16 right-[10%] w-2.5 h-2.5 rounded-full dot-yellow" />
    </div>
  );
}
